import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";
import {
  getCoachToken,
  hasCoachSession,
  isStoredJwtSession,
  loginPathFor,
  refreshCoachToken,
} from "api/coachAuth";

interface ErrorResponse {
  code: number;
  debugMessage: string;
  message: string;
}
const HOST_API = process.env.REACT_APP_API_SERVER_HOST;
const API_KEY = process.env.REACT_APP_API_KEY;

const axiosInstance = (token?: string) => {
  // 코치 JWT 세션이면 Bearer, 아니면 레거시 X-Auth-Token (인자 또는 빈 값).
  const coachToken = getCoachToken();
  const instance = axios.create({
    withCredentials: true,
    baseURL: HOST_API,
    headers: {
      ...(coachToken
        ? { Authorization: `Bearer ${coachToken}` }
        : { "X-Auth-Token": token || "" }),
      "X-Api-Key": API_KEY,
      "content-type": "application/json",
    },
  });

  const onFulfilled = (response: AxiosResponse) => response;

  const onRejected = async (error: AxiosError<ErrorResponse>) => {
    if (error.response?.status === 401) {
      // 코치 JWT 세션: 재발급 후 1회 재시도, 실패하면 코치 로그인으로.
      if (hasCoachSession() || isStoredJwtSession()) {
        const original = error.config as
          | (typeof error.config & { _coachRetry?: boolean })
          | undefined;
        if (original && !original._coachRetry) {
          original._coachRetry = true;
          const newToken = await refreshCoachToken();
          if (newToken) {
            original.headers.Authorization = `Bearer ${newToken}`;
            return instance(original);
          }
        }
        window.location.href = loginPathFor(null);
        return Promise.reject(error);
      }
      window.location.href = loginPathFor(null);
    }

    return Promise.reject(error);
  };

  instance.interceptors.response.use(onFulfilled, onRejected);

  return instance;
};

export default axiosInstance;
